import {
  Body1,
  Button,
  Dropdown,
  Field,
  Input,
  Option,
  Spinner,
  Switch,
  Text,
  Title3,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { NsPayrollItem } from '@worklogix/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SHRINKABLE } from '../responsive';

import { useTimeAdminApi } from './api';
import {
  CONNECTION_MODES,
  ROUNDING_RULES,
  canEnablePosting,
  isConnectionFormValid,
  toFormState,
  toUpdateInput,
  validateConnectionForm,
} from './connectionForm';
import type { ConnectionFormState } from './connectionForm';
import { toErrorMessage } from './errors';
import { payrollLabel, toPayrollOptions } from './payrollOptions';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    // SHRINKABLE first: its maxWidth:'100%' is the floor, the 640px reading
    // width below it is the cap. Order matters — reversed, the form would refuse
    // to go under 640px and overflow a phone.
    ...SHRINKABLE,
    maxWidth: '640px',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  gate: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    border: `${tokens.strokeWidthThick} solid ${tokens.colorPaletteRedBorder2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  gateOn: {
    border: `${tokens.strokeWidthThick} solid ${tokens.colorPaletteGreenBorder2}`,
    backgroundColor: tokens.colorPaletteGreenBackground1,
  },
  caption: {
    color: tokens.colorNeutralForeground3,
  },
  secrets: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
  },
  ok: {
    color: tokens.colorPaletteGreenForeground1,
  },
});

const PAYROLL_NONE = '';

/**
 * NS Connection tab (Admin) — view/edit the tenant's NON-SECRET NetSuite
 * configuration and flip the human WRITE GATE (`postingEnabled`). The TBA secret
 * names + vault URI live server-side only (resolved via managed identity) and are
 * never surfaced here — the tab shows a static Key Vault note in their place.
 * Consumes Dev-B's `GET`/`PATCH /netsuite/connection`.
 */
export function NsConnectionTab(): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();

  const [form, setForm] = useState<ConnectionFormState | null>(null);
  const [payrollItems, setPayrollItems] = useState<NsPayrollItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const [syncingPayroll, setSyncingPayroll] = useState(false);
  const [payrollMessage, setPayrollMessage] = useState<string | null>(null);

  const loadPayroll = useCallback(() => {
    void api
      .listPayrollItems()
      .then((items) => setPayrollItems(items))
      .catch(() => {
        // The picker is a convenience; a failure here leaves the raw id editable
        // via the field and does not block saving the rest of the config.
      });
  }, [api]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    void api
      .getNsConnection()
      .then((res) => {
        if (active) {
          setForm(toFormState(res));
        }
      })
      .catch((err: unknown) => {
        if (active) {
          setError(toErrorMessage(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => loadPayroll(), [loadPayroll]);

  const patch = useCallback((next: Partial<ConnectionFormState>) => {
    setForm((prev) => (prev === null ? prev : { ...prev, ...next }));
    setSaveMessage(null);
  }, []);

  const errors = useMemo(() => (form === null ? {} : validateConnectionForm(form)), [form]);
  const payrollOptions = useMemo(() => toPayrollOptions(payrollItems), [payrollItems]);

  const handleSave = useCallback(async () => {
    if (form === null || !isConnectionFormValid(validateConnectionForm(form))) {
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSaveMessage(null);
    try {
      const res = await api.updateNsConnection(toUpdateInput(form));
      setForm(toFormState(res));
      setSaveMessage('Connection saved.');
    } catch (err: unknown) {
      setSaveError(toErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }, [api, form]);

  const handleSyncPayroll = useCallback(async () => {
    setSyncingPayroll(true);
    setPayrollMessage(null);
    try {
      const res = await api.syncPayrollItems();
      setPayrollMessage(`Synced payroll items (${res.upserted} upserted).`);
      loadPayroll();
    } catch (err: unknown) {
      setPayrollMessage(toErrorMessage(err));
    } finally {
      setSyncingPayroll(false);
    }
  }, [api, loadPayroll]);

  if (loading || form === null) {
    return error !== null ? (
      <Text className={styles.error} role="alert">
        {error}
      </Text>
    ) : (
      <Spinner label="Loading connection…" />
    );
  }

  const gateAllowed = canEnablePosting(form);
  const payrollValue = payrollLabel(payrollItems, form.defaultPayrollItemId);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Title3>NetSuite connection</Title3>
        <Body1 className={styles.caption}>
          Non-secret configuration for posting approved hours to NetSuite Time Tracking. Secret
          values are never shown or requested here.
        </Body1>
      </div>

      <div className={styles.form}>
        <Field label="Mode">
          <Dropdown
            value={CONNECTION_MODES.find((m) => m.value === form.mode)?.label ?? form.mode}
            selectedOptions={[form.mode]}
            onOptionSelect={(_, d) => {
              if (d.optionValue === 'local' || d.optionValue === 'netsuite') {
                patch({ mode: d.optionValue });
              }
            }}
          >
            {CONNECTION_MODES.map((m) => (
              <Option key={m.value} value={m.value} text={m.label}>
                {m.label}
              </Option>
            ))}
          </Dropdown>
        </Field>

        <div className={form.postingEnabled ? `${styles.gate} ${styles.gateOn}` : styles.gate}>
          <Switch
            checked={form.postingEnabled}
            disabled={!gateAllowed && !form.postingEnabled}
            label={
              form.postingEnabled ? 'Posting to NetSuite is ON' : 'Post real time to NetSuite (OFF)'
            }
            onChange={(_, d) => patch({ postingEnabled: d.checked })}
          />
          <Text size={200} className={styles.caption}>
            When ON, approving hours writes REAL NetSuite TimeBill records for this tenant. Leave
            OFF until the connection has been validated in this environment. Requires NetSuite mode
            with an account and REST host.
          </Text>
          {errors.postingEnabled !== undefined ? (
            <Text size={200} className={styles.error} role="alert">
              {errors.postingEnabled}
            </Text>
          ) : null}
        </div>

        <Field
          label="Account / realm"
          {...(errors.account !== undefined
            ? { validationState: 'error' as const, validationMessage: errors.account }
            : {})}
        >
          <Input
            value={form.account}
            placeholder="5399989_SB1"
            onChange={(_, d) => patch({ account: d.value })}
          />
        </Field>

        <Field
          label="REST host"
          {...(errors.restHost !== undefined
            ? { validationState: 'error' as const, validationMessage: errors.restHost }
            : {})}
        >
          <Input
            value={form.restHost}
            placeholder="5399989-sb1.suitetalk.api.netsuite.com"
            onChange={(_, d) => patch({ restHost: d.value })}
          />
        </Field>

        <Field label="Subsidiary id">
          <Input
            value={form.subsidiaryId}
            placeholder="1"
            onChange={(_, d) => patch({ subsidiaryId: d.value })}
          />
        </Field>

        <Field
          label="Default location id"
          hint="Fallback when a department has no location override."
        >
          <Input
            value={form.defaultLocationId}
            onChange={(_, d) => patch({ defaultLocationId: d.value })}
          />
        </Field>

        <Field
          label="Default payroll item"
          hint="Fallback earning type when an employee has none mapped."
        >
          <Dropdown
            value={payrollValue === '' ? 'None' : payrollValue}
            selectedOptions={[form.defaultPayrollItemId]}
            onOptionSelect={(_, d) =>
              patch({ defaultPayrollItemId: d.optionValue ?? PAYROLL_NONE })
            }
          >
            <Option value={PAYROLL_NONE} text="None">
              None
            </Option>
            {payrollOptions.map((o) => (
              <Option key={o.value} value={o.value} text={o.label}>
                {o.label}
              </Option>
            ))}
          </Dropdown>
        </Field>

        <Field label="Custom form id">
          <Input
            value={form.customForm}
            placeholder="-400"
            onChange={(_, d) => patch({ customForm: d.value })}
          />
        </Field>

        <Field label="Time type">
          <Input
            value={form.timeType}
            placeholder="A"
            onChange={(_, d) => patch({ timeType: d.value })}
          />
        </Field>

        <Field label="Rounding rule">
          <Dropdown
            value={
              ROUNDING_RULES.find((r) => r.value === form.roundingRule)?.label ?? form.roundingRule
            }
            selectedOptions={[form.roundingRule]}
            onOptionSelect={(_, d) => {
              const match = ROUNDING_RULES.find((r) => r.value === d.optionValue);
              if (match !== undefined) {
                patch({ roundingRule: match.value });
              }
            }}
          >
            {ROUNDING_RULES.map((r) => (
              <Option key={r.value} value={r.value} text={r.label}>
                {r.label}
              </Option>
            ))}
          </Dropdown>
        </Field>

        <Field
          label="Payroll timezone"
          hint="IANA timezone the business day is computed in (e.g. America/Chicago). Evening shifts attribute to the correct payroll day, not UTC."
        >
          <Input
            value={form.payrollTimezone}
            placeholder="America/Chicago"
            onChange={(_, d) => patch({ payrollTimezone: d.value })}
          />
        </Field>

        <Field
          label="Default payroll item fallback"
          hint="When ON, a group whose employee has no mapped earning type falls back to the default payroll item above. When OFF (recommended), such a group is skipped rather than posted under a guessed earning type."
        >
          <Switch
            checked={form.allowDefaultPayrollItem}
            label={
              form.allowDefaultPayrollItem
                ? 'Fallback to the default payroll item is ON'
                : 'Skip unmapped earning types (fallback OFF)'
            }
            onChange={(_, d) => patch({ allowDefaultPayrollItem: d.checked })}
          />
        </Field>

        <div className={styles.actions}>
          <Button
            appearance="primary"
            onClick={() => void handleSave()}
            disabled={saving || !isConnectionFormValid(errors)}
          >
            {saving ? <Spinner size="tiny" /> : 'Save connection'}
          </Button>
          <Button
            appearance="secondary"
            onClick={() => void handleSyncPayroll()}
            disabled={syncingPayroll}
          >
            {syncingPayroll ? <Spinner size="tiny" /> : 'Sync payroll items'}
          </Button>
        </div>

        {saveMessage !== null ? (
          <Text className={styles.ok} role="status">
            {saveMessage}
          </Text>
        ) : null}
        {saveError !== null ? (
          <Text className={styles.error} role="alert">
            {saveError}
          </Text>
        ) : null}
        {payrollMessage !== null ? <Text role="status">{payrollMessage}</Text> : null}
      </div>

      <div className={styles.secrets}>
        <Text weight="semibold">TBA secrets</Text>
        <Text size={200} className={styles.caption}>
          The NetSuite TBA credentials are managed in Azure Key Vault and resolved by the API via
          managed identity. Their names and values are never stored, shown, or requested here.
        </Text>
      </div>
    </div>
  );
}
